import {
  Component,
  EventEmitter,
  Output,
  Signal,
  computed,
  effect,
  signal,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Utils } from '../../../core/utils/password-strength.util';
import { UserOnboarding } from '../../../core/models/user-onboarding.model';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RegistrationService } from '../../../core/services/registration.service';
import { Router, RouterLink } from '@angular/router';
import { NotificationService } from '../../../core/services/notification.service';
import { strongPasswordValidator } from '../../../core/validators/password.validator';

function matchPasswords(group: AbstractControl): ValidationErrors | null {
  const p = group.get('password')?.value as string;
  const c = group.get('confirmPassword')?.value as string;
  return p && c && p !== c ? { passwordMismatch: true } : null;
}

import { TermsAndConditionsModal } from '../../terms-and-conditions-modal/terms-and-conditions-modal';

@Component({
  selector: 'user-onboarding',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltip,
    RouterLink,
    TermsAndConditionsModal,],
  providers: [FormBuilder],
  templateUrl: './user-onboarding-component.html',
  styleUrls: ['./user-onboarding-component.scss'],
})
export class UserOnboardingComponent implements OnInit {
  @Output() submitStep = new EventEmitter<UserOnboarding>();
  form!: FormGroup;

  hidePassword = signal(true);
  hideConfirm = signal(true);

  isLoading = false;
  errorMessage: string | null = null;

  constructor(
    public fb: FormBuilder,
    private registrationService: RegistrationService,
    private notification: NotificationService,
    private router: Router,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit() {
    this.form = this.fb.group(
      {
        firstName: ['', [Validators.required, Validators.maxLength(100)]],
        lastName: ['', [Validators.required, Validators.maxLength(100)]],
        email: ['', [Validators.required, Validators.email, Validators.maxLength(120)]],
        cellNumber: ['', [Validators.required]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            strongPasswordValidator(),
          ],
        ],
        confirmPassword: ['', [Validators.required]],
        acceptPrivacy: [false, Validators.requiredTrue],
      },
      { validators: matchPasswords },
    );
  }

  // @ts-ignore
  passwordStrength: Signal<number> = computed(() =>
    Utils.passwordStrength(this.form.get('password')?.value),
  );

  submitUserOnboarding() {
    this.errorMessage = null;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    let user = {
      firstName: this.form.get('firstName')?.value,
      lastName: this.form.get('lastName')?.value,
      password: this.form.get('password')?.value,
      confirmPassword: this.form.get('confirmPassword')?.value,
      email: this.form.get('email')?.value,
      cellNumber: this.form.get('cellNumber')?.value,
    };

    this.registrationService.registerUser(user).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.notification.success(
          'Account created successfully! Please verify your contact.',
          'Registration Successful',
        );
        this.router.navigate(['otp-verification', res]);
      },
      error: (error) => {
        this.isLoading = false;
        const msg =
          error?.error?.message || 'Registration could not be completed. Please try again.';
        this.errorMessage = msg;
        this.notification.error(msg, 'Registration Failed');
      },
    });
  }

  toggle(field: 'password' | 'confirm'): void {
    if (field === 'password') this.hidePassword.set(!this.hidePassword());
    else this.hideConfirm.set(!this.hideConfirm());
  }

  hasUppercase(): boolean {
    return /[A-Z]/.test(this.form.get('password')?.value);
  }

  hasLowercase(): boolean {
    return /[a-z]/.test(this.form.get('password')?.value);
  }

  hasNumber(): boolean {
    return /\d/.test(this.form.get('password')?.value);
  }

  hasSpecial(): boolean {
    return /[^A-Za-z0-9]/.test(this.form.get('password')?.value);
  }

  getPasswordRequirementsHtml(password: string): SafeHtml {
    const html = `
      <ul class="small text-muted mb-0 ps-3">
        <li class="${password?.length >= 8 ? 'text-success' : ''}">8+ chars</li>
        <li class="${/[A-Z]/.test(password) ? 'text-success' : ''}">Uppercase</li>
        <li class="${/[a-z]/.test(password) ? 'text-success' : ''}">Lowercase</li>
        <li class="${/\d/.test(password) ? 'text-success' : ''}">Number</li>
        <li class="${/[^A-Za-z0-9]/.test(password) ? 'text-success' : ''}">Special char (!@#$...)</li>
      </ul>
    `;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
